import React, { useEffect, useMemo, useRef, useState } from "react";

const defaultMetrics = [
  {
    value: 6851.3,
    suffix: " Cr.",
    prefix: "₹",
    label: "Asset Under Management",
    full: "₹6,851.30 Crore",
  },
  {
    value: 20,
    suffix: " Lakh+",
    label: "Customers",
    full: "20,00,000+",
  },
  {
    value: 1498,
    label: "Branches",
  },
  {
    value: 16,
    label: "States",
  },
  {
    value: 12000,
    suffix: "+",
    label: "Employees",
  },
];

const formatValue = (value, prefix = "", suffix = "") => {
  const formatter = new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: Number.isInteger(value) ? 0 : 1,
  });
  const formatted = formatter.format(value);
  return `${prefix ?? ""}${formatted}${suffix ?? ""}`.replace(
    /(\d)(\s)/g,
    "$1\u00a0"
  );
};

const ImpactMetricsStrip = ({ metrics = defaultMetrics }) => {
  const [hasAnimated, setHasAnimated] = useState(false);
  const [counts, setCounts] = useState(
    metrics.map(() => ({ displayValue: 0, done: false }))
  );
  const containerRef = useRef(null);

  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const validMetrics = useMemo(
    () => metrics.filter((item) => item?.value || item?.value === 0),
    [metrics]
  );

  // Intersection Observer: trigger animation
  useEffect(() => {
    if (prefersReducedMotion) {
      setCounts(
        validMetrics.map((metric) => ({
          displayValue: metric.value,
          done: true,
        }))
      );
      setHasAnimated(true);
      return;
    }

    const node = containerRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
          }
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasAnimated, prefersReducedMotion, validMetrics]);

  // Number counting animation
  useEffect(() => {
    if (!hasAnimated || prefersReducedMotion) return;

    const animations = validMetrics.map((metric, index) => {
      const duration = 1200 + index * 100;
      const start = performance.now();

      const animate = (timestamp) => {
        const elapsed = timestamp - start;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = 1 - Math.pow(1 - progress, 2);
        const value = metric.value * easedProgress;

        setCounts((prev) => {
          const next = [...prev];
          next[index] = {
            displayValue:
              metric.value < 10 ? Number(value.toFixed(1)) : Math.round(value),
            done: progress === 1,
          };
          return next;
        });

        if (progress < 1) requestAnimationFrame(animate);
      };

      requestAnimationFrame(animate);
      return null;
    });

    return () => animations.forEach((a) => a && cancelAnimationFrame(a));
  }, [hasAnimated, prefersReducedMotion, validMetrics]);

  return (
    <section
      ref={containerRef}
      className="w-full bg-[#1F1F1F] border-t py-5 sm:py-14"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-nowrap items-stretch gap-4 sm:gap-6 lg:gap-0 overflow-x-auto lg:overflow-visible">
          {validMetrics.map((metric, index) => {
            const displayValue = counts[index]?.displayValue ?? metric.value;
            const finalValue =
              counts[index]?.done || prefersReducedMotion
                ? metric.value
                : displayValue;
            const isLast = index === validMetrics.length - 1;
            return (
              <div
                key={metric.label}
                className={`group flex flex-col items-center text-center transition-transform hover:scale-105 py-6 sm:py-[29px] px-3 sm:px-[5px] relative min-w-[150px] sm:min-w-[160px] lg:min-w-0 lg:flex-1
                ${isLast ? "" : "counter"}
                `}
                style={{
                  animation: hasAnimated
                    ? `fadeUp 400ms ease-out ${index * 100}ms both`
                    : "none",
                }}
              >
                <div
                  className="text-white font-normal text-xl sm:text-3xl lg:text-4xl leading-tight whitespace-normal sm:whitespace-nowrap"
                  style={{
                    textShadow: "0 8px 20px rgba(0,0,0,0.35)",
                  }}
                  aria-label={
                    metric.full
                      ? `${metric.full}, ${metric.label}`
                      : `${finalValue}, ${metric.label}`
                  }
                  title={metric.full}
                >
                  {formatValue(finalValue, metric.prefix, metric.suffix)}
                </div>

                <span className="block h-[1px] w-8 sm:w-10 bg-red mx-auto mt-2 group-hover:w-16 sm:group-hover:w-20 transition-all duration-300 ease-out"></span>

                <p className="mt-2 text-[10px] sm:text-xs uppercase tracking-wider text-white/70 font-light">
                  {metric.label}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="fadeUp"] {
            animation: none !important;
          }
        }
        .bg-primary {
          background-color: #00C389; /* customize your accent color */
        }
      `}</style>
    </section>
  );
};

export default ImpactMetricsStrip;
